import { useEffect } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { toast } from "sonner"

import { downloadCitationFileApi, fetchCitationFileContentApi } from "@/api/api"
import { isLiveAuthorizedFilthContentUrl } from "@/api/client/citations"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

/**
 * Hook to fetch citation file content
 */
export function useCitationFile(url: string | undefined, enabled = true) {
  const settingsService = SettingsService.getInstance()

  return useQuery({
    queryKey: queryKeys.citationFile(url || ""),
    queryFn: () => fetchCitationFileContentApi(url!, settingsService.token),
    enabled: enabled && !!url,
    staleTime: 1000 * 60 * 10, // 10 minutes - citations rarely change
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Mutation hook to download a citation file
 * Downloads are mutations (user actions), not queries
 */
export function useCitationFileDownload() {
  const settingsService = SettingsService.getInstance()

  const mutation = useMutation({
    mutationFn: async (url: string) => {
      const blob = await downloadCitationFileApi(url, settingsService.token)
      return blob
    },
    gcTime: 0,
    onError: (error: Error) => {
      toast.error("Failed to download file", {
        description: error.message || "An error occurred",
      })
    },
  })

  useEffect(() => {
    if (
      mutation.data !== undefined &&
      typeof mutation.variables === "string" &&
      isLiveAuthorizedFilthContentUrl(mutation.variables)
    ) {
      mutation.reset()
    }
  }, [mutation.data, mutation.reset, mutation.variables])

  return mutation
}
